import { BACKUP_VERSION, clearSyncCursor, planSelectiveClear, stripDeviceMutState, type BackupEnvelope, type BackupSelection } from "@app/core";
import { bulkUpsert, yieldToTask, type BulkUpsertRows } from "@app/storage";
import { getServices } from "../platform.js";
import { useTasksStore } from "../stores/tasks.store.js";
import { useLabelsStore } from "../stores/labels.store.js";
import { useTopicsStore } from "../stores/topics.store.js";
import { useCalendarsStore } from "../stores/calendars.store.js";

/** Snapshot the live data graph straight from the in-memory zustand stores —
 *  NO repository reads. The stores already mirror the active tasks/rules/
 *  overrides/labels/topics/calendars, so this is synchronous and instant.
 *  One-step undo uses this so capturing the pre-mutation state doesn't cost a
 *  round of DB reads on the critical path of every edit (the desktop SQLite
 *  adapter makes each repo read an IPC hop — six of them per edit was the bulk
 *  of the perceived edit latency). `gatherSnapshot` (async, repo-backed) stays
 *  for backup-export, where completeness over the on-disk rows matters more. */
export function gatherSnapshotSync(): BackupEnvelope {
  const t = useTasksStore.getState();
  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    tasks: t.tasks,
    recurrenceRules: Object.values(t.rules),
    recurrenceOverrides: Object.values(t.overrides).flat(),
    labels: useLabelsStore.getState().labels,
    topics: useTopicsStore.getState().topics,
    calendars: useCalendarsStore.getState().calendars.map(clearSyncCursor),
  };
}

/** Snapshot the live (active) data graph — tasks + recurrence rules + overrides +
 *  labels + topics + calendars — into a versioned envelope. Reads from the
 *  repositories (authoritative on-disk rows). Used by backup-export. */
export async function gatherSnapshot(): Promise<BackupEnvelope> {
  const { tasks, recurrence, recurrenceOverrides, labels, topics, calendars, themes } = getServices();
  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    // The `mut` counters are device-local change tracking (see core
    // sync/device-local.ts) — a backup restored on another device must not carry
    // this device's bookkeeping (restore's bulk writes own the counters anyway).
    tasks: (await tasks.listActive()).map(stripDeviceMutState),
    recurrenceRules: (await recurrence.listActive()).map(stripDeviceMutState),
    recurrenceOverrides: (await recurrenceOverrides.listAllActive()).map(stripDeviceMutState),
    labels: (await labels.listActive()).map(stripDeviceMutState),
    topics: (await topics.listActive()).map(stripDeviceMutState),
    calendars: (await calendars.listActive()).map((c) => stripDeviceMutState(clearSyncCursor(c))),
    themes: await themes.listActive(),
  };
}

/** Write envelope rows in dependency order so referenced rows exist first.
 *  UPSERT semantics with a fresh `updated_at` (see {@link planUpserts}): a plain
 *  INSERT hit a UNIQUE violation when an imported id survived locally as a
 *  tombstone (backup made → task deleted → backup re-imported), and the fresh
 *  stamp is what lets an imported row win LWW against the server's older copy
 *  (`bulkUpsert` itself bumps each row's `mut`, so both sync engines see the
 *  import as local writes to send). Writes go through `bulkUpsert` so a large
 *  native batches, not one IPC round-trip per row. */
export async function insertAll(env: BackupEnvelope, onProgress?: BatchProgress): Promise<void> {
  const { tasks, recurrence, recurrenceOverrides, labels, topics, calendars, themes, db } = getServices();
  const now = new Date().toISOString();
  const writes: BulkUpsertRows = {
    calendars: await planUpsertsChunked(env.calendars ?? [], await calendars.listAll(), now),
    topics: await planUpsertsChunked(env.topics ?? [], await topics.listAll(), now),
    labels: await planUpsertsChunked(env.labels, await labels.listAll(), now),
    recurrenceRules: await planUpsertsChunked(env.recurrenceRules, await recurrence.listAll(), now),
    tasks: await planUpsertsChunked(env.tasks, await tasks.listAll(), now),
    recurrenceOverrides: await planUpsertsChunked(env.recurrenceOverrides, await recurrenceOverrides.listAll(), now),
  };
  await bulkUpsert(db, writes, { yieldBetweenBatches: true, onProgress });
  // Themes have no bulk statement builder; a backup carries at most one row.
  await upsertRowsViaRepo(themes, env.themes ?? [], now);
}

/**
 * SOFT-delete only the data categories the user chose to REPLACE, leaving
 * unchecked categories untouched. Tasks and events share one table, so they're
 * removed by id per `is_event`; a removed series' rules + overrides go with it.
 * Tombstones (not hard deletes) so a signed-in user's replace propagates the
 * removals to the sync server — a hard delete left nothing to push and the
 * cleared rows resurrected on the next pull. Rows the backup re-imports are
 * revived by `insertAll`'s upsert. Used by the selective "Replace from backup"
 * path (NOT undo — diff-based restore below — or delete-all via `wipeData`).
 */
export async function clearSelectedCategories(sel: BackupSelection, onProgress?: BatchProgress): Promise<void> {
  const { tasks, recurrence, recurrenceOverrides, labels, topics, calendars, db } = getServices();
  const ids = planSelectiveClear(await gatherSnapshot(), sel);
  const now = new Date().toISOString();
  const writes: BulkUpsertRows = {
    calendars: planTombstones(await calendars.listAll(), ids.calendarIds, now),
    topics: planTombstones(await topics.listAll(), ids.topicIds, now),
    labels: planTombstones(await labels.listAll(), ids.labelIds, now),
    recurrenceRules: planTombstones(await recurrence.listAll(), ids.ruleIds, now),
    tasks: planTombstones(await tasks.listAll(), ids.taskIds, now),
    recurrenceOverrides: planTombstones(await recurrenceOverrides.listAll(), ids.overrideIds, now),
  };
  await bulkUpsert(db, writes, { yieldBetweenBatches: true, onProgress });
}

/** Progress reporter for the chunked import/restore write paths. */
export type BatchProgress = (done: number, total: number) => void;

/** Rows planned per slice before the main thread is handed back. 1,000 rows of
 *  `planUpserts` is ~10 ms — small enough that a frame is never missed, large
 *  enough that the yields themselves are not the cost. */
const PLAN_CHUNK = 1000;

function planTombstones<T extends RestoreRow>(current: T[], ids: Iterable<string>, now: string): T[] {
  const wanted = new Set(ids);
  const out: T[] = [];
  if (wanted.size === 0) return out;
  for (const row of current) {
    if (!wanted.has(row.id) || row.deleted_at) continue;
    out.push({ ...row, deleted_at: now, updated_at: now });
  }
  return out;
}

/** Reload the task + label + topic + calendar stores from the repositories. */
export async function rehydrateStores(): Promise<void> {
  await useCalendarsStore.getState().hydrate();
  await useTopicsStore.getState().hydrate();
  await useLabelsStore.getState().hydrate();
  await useTasksStore.getState().hydrate();
}

/** Version/cursor metadata excluded from the content diff: `rev`/`updated_at`
 *  are sync bookkeeping, the calendar sync-cursor pair is device-local (the
 *  snapshot stores it cleared via `clearSyncCursor`), and the `mut` counters are
 *  device-local change tracking the repositories own — live rows carry them,
 *  snapshot rows don't, and either way they are never "content". */
const RESTORE_IGNORED_FIELDS = new Set([
  "updated_at", "rev", "sync_token", "last_synced_at",
  "mut", "server_acked_mut", "google_acked_mut",
]);
const CURSOR_FIELDS = ["sync_token", "last_synced_at"] as const;

function restoreContentKey(row: object): string {
  return JSON.stringify(
    Object.entries(row as Record<string, unknown>)
      .filter(([k]) => !RESTORE_IGNORED_FIELDS.has(k))
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}

interface RestoreRow {
  id: string;
  updated_at: string;
  deleted_at: string | null;
}
interface RestoreRepo<T extends RestoreRow> {
  insert(row: T): Promise<void>;
  update(row: T): Promise<void>;
  listAll(): Promise<T[]>;
}

/** Rows to write so `rows` WIN sync LWW: unchanged rows are skipped entirely; a
 *  new/changed row is stamped `updated_at = now`, adopting the CURRENT row's
 *  `rev` (rev ties, newer updated_at wins — a resurrected tombstone must
 *  outrank the server's delete) and the device-local calendar sync cursors
 *  (envelope/snapshot copies are cleared). Pure — the caller batch-writes. */
function planUpserts<T extends RestoreRow>(rows: T[], current: T[], now: string): T[] {
  const curById = new Map(current.map((r) => [r.id, r]));
  const out: T[] = [];
  for (const row of rows) planUpsertRow(out, row, curById, now);
  return out;
}

function planUpsertRow<T extends RestoreRow>(out: T[], row: T, curById: Map<string, T>, now: string): void {
  const cur = curById.get(row.id);
  if (cur && restoreContentKey(row) === restoreContentKey(cur)) return;
  const next = { ...row, updated_at: now } as unknown as Record<string, unknown>;
  if (cur) {
    const c = cur as unknown as Record<string, unknown>;
    if ("rev" in c) next["rev"] = c["rev"];
    for (const k of CURSOR_FIELDS) if (k in c) next[k] = c[k];
  }
  out.push(next as unknown as T);
}

/** {@link planUpserts}, sliced so the main thread comes up for air. Diffing a
 *  12.5k-row table is ~150 ms of `JSON.stringify` on its own — enough to be the
 *  dominant long task once the storage writes stopped being quadratic. */
async function planUpsertsChunked<T extends RestoreRow>(rows: T[], current: T[], now: string): Promise<T[]> {
  const curById = new Map(current.map((r) => [r.id, r]));
  const out: T[] = [];
  for (let i = 0; i < rows.length; i += PLAN_CHUNK) {
    const until = Math.min(i + PLAN_CHUNK, rows.length);
    for (let j = i; j < until; j++) planUpsertRow(out, rows[j]!, curById, now);
    if (until < rows.length) await yieldToTask();
  }
  return out;
}

/** Repo-loop twin of the bulk path for tables without a bulk statement builder
 *  (themes). Same plan/skip semantics. */
async function upsertRowsViaRepo<T extends RestoreRow>(repo: RestoreRepo<T>, rows: T[], now: string): Promise<void> {
  const current = await repo.listAll();
  const curIds = new Set(current.map((r) => r.id));
  for (const row of planUpserts(rows, current, now)) {
    if (curIds.has(row.id)) await repo.update(row);
    else await repo.insert(row);
  }
}

/** Rows converging one table onto the snapshot with COMPENSATING EDITS (see
 *  {@link restoreSnapshot}): the snapshot's new/changed rows, plus soft-deletes
 *  for rows created after the snapshot. Unchanged rows aren't touched at all. */
function planRestore<T extends RestoreRow>(envRows: T[], current: T[], now: string): T[] {
  const writes = planUpserts(envRows, current, now);
  const envIds = new Set(envRows.map((r) => r.id));
  for (const cur of current) {
    if (envIds.has(cur.id) || cur.deleted_at) continue;
    // Created after the snapshot → SOFT-delete so the tombstone syncs. (The old
    // hard wipe left nothing behind, so an add that had already auto-pushed
    // resurrected from the server on the next pull.) Pre-existing tombstones
    // are skipped above: the snapshot only holds active rows, and wiping them
    // would orphan a not-yet-pushed delete.
    writes.push({ ...cur, deleted_at: now, updated_at: now });
  }
  return writes;
}

/** Converge the persisted data back onto the pre-gesture snapshot, then
 *  rehydrate the stores. Used by one-step undo. This is DIFF-based, not
 *  wipe-and-reinsert: reinserting stale rows verbatim would carry old
 *  `updated_at`/`rev`, so for a signed-in user the server's copy would win the
 *  next sync merge and silently revert the undo — instead every difference is
 *  written as a fresh edit (or tombstone) that outranks the server copy and
 *  pushes cleanly. Settings are never touched. */
export async function restoreSnapshot(env: BackupEnvelope): Promise<void> {
  const { tasks, recurrence, recurrenceOverrides, labels, topics, calendars, db } = getServices();
  const now = new Date().toISOString();
  // Dependency order (bulkUpsert preserves it) so references exist first.
  const writes: BulkUpsertRows = {
    calendars: planRestore(env.calendars ?? [], await calendars.listAll(), now),
    topics: planRestore(env.topics ?? [], await topics.listAll(), now),
    labels: planRestore(env.labels, await labels.listAll(), now),
    recurrenceRules: planRestore(env.recurrenceRules, await recurrence.listAll(), now),
    tasks: planRestore(env.tasks, await tasks.listAll(), now),
    recurrenceOverrides: planRestore(env.recurrenceOverrides, await recurrenceOverrides.listAll(), now),
  };
  await bulkUpsert(db, writes);
  await rehydrateStores();
}
